// TalkingParents "Complete Message Record" PDF export → canonical corpus.csv.
//
// Direct port of evals/adapters/talking_parents.py — keep the parsing in
// lockstep so a TalkingParents PDF run through the browser yields the same
// canonical rows as the desktop / Python adapter (parity is validated in
// talking_parents.test.ts against the Python reference from the same
// tiny-sample.pdf).
//
// The record is a certified export: cover page (certification prose + the
// 16-digit Unique Authentication Code), a Case Information page, then the
// Messages section. Messages are grouped into subjects (threads); subjects
// are ordered by most-recent activity (NOT globally chronological), messages
// within a subject are chronological. Every page carries a 4-line footer.
// Once the footer is stripped, bodies flow cleanly across page breaks (no
// OFW-style reprint dedup). pd.js and pdfplumber agree on every structural
// line here except the cover's two-column code block, which is front matter
// (verified against a real 742-page record, 2026-07-10).
//
// Timezone: the record declares its display zone as a Windows/.NET zone ID
// ("Pacific Standard Time" — the producer is GemBox.Pdf/.NET). Despite the
// "Standard" wording these IDs carry full DST rules, so the label names a
// zone, not a fixed offset. We map the label to its IANA zone (CLDR
// windowsZones subset), localize each naive wall-clock timestamp (DST-aware),
// and store UTC ISO. An explicit override wins; an unmapped label falls back
// to America/New_York and flags the assumption loudly.
import { DateTime, IANAZone } from "luxon";

// Windows/.NET TimeZoneInfo ID → IANA. Pure data shared with the Python
// adapter via a SINGLE-SOURCE JSON (the bench/cost-estimate-constants.json
// pattern) so the two engines cannot drift.
import windowsTimezones from "../../../../evals/adapters/windows_timezones.json";

import { sanitizeActor, sanitizeBody } from "./_normalize";
import { extractPdfTextEol, type PdfExtractProgress } from "./_pdf";

export const PARSER_VERSION = "0.1.0";
export const ADAPTER_NAME = "talking-parents";

// Records whose zone label we can't map fall back here (largest US population
// share), flagged loudly. Lockstep with talking_parents.py.
export const DEFAULT_ASSUMED_TIMEZONE = "America/New_York";

// Case-insensitive lookup; keys in the shared JSON are lowercase, "_"-keys
// are file comments.
const TZ_LABEL_TO_IANA: Record<string, string> = Object.fromEntries(
  Object.entries(windowsTimezones as Record<string, string>).filter(
    ([k]) => !k.startsWith("_"),
  ),
);

const DATE_SRC = String.raw`\d{1,2}/\d{1,2}/\d{4}`;
const TIME_SRC = String.raw`\d{1,2}:\d{2}:\d{2} [AP]M`;

// Message header: "<Name> on <date> at <time> said:". Machine-generated and
// full-line; a body line matches only if it is itself a verbatim header
// shape, and then only a date-valid one splits (see parseMdyHms). Lockstep
// with talking_parents.py _SAID_RE etc.
const SAID_RE = new RegExp(`^(.+?) on (${DATE_SRC}) at (${TIME_SRC}) said:\\s*$`);
const CREATED_RE = new RegExp(
  `^Created by: (.+?) on (${DATE_SRC}) at (${TIME_SRC})\\s*$`,
);
const VIEWED_RE = new RegExp(
  `^(.+?) viewed this subject on (${DATE_SRC}) at (${TIME_SRC})\\s*$`,
);
const DOWNLOADED_RE = new RegExp(
  `^(.+?) downloaded \\[(.+?)\\] on (${DATE_SRC}) at (${TIME_SRC})\\s*$`,
);
// Attachment line — requires the trailing "(<size>)" so prose that merely
// starts with "Attached File(s):" can't silently eat a body line. Sizes
// observed in the wild: "12.5 KB", "3.4 MB", "0 Bytes".
const SIZE_SRC = String.raw`\d+(?:\.\d+)?\s?(?:Bytes|B|KB|MB|GB)`;
const ATTACH_RE = new RegExp(
  String.raw`^Attached File\(s\): (.+\((?:${SIZE_SRC})\))\s*$`,
);
const ATT_ITEM_RE = new RegExp(
  String.raw`(.+?)\s*\((${SIZE_SRC})\)(?:,\s*|$)`,
  "g",
);
// Multi-attachment messages list files ONE PER LINE under the Attached
// File(s) line ("IMG_0693.png (4.98 MB)"), possibly flowing across a page
// break. A continuation item is a FULL line of exactly "name (size)" —
// consumed only while directly adjacent to the attachment list (2026-07-11
// fidelity review, finding B2).
const ATT_CONT_RE = new RegExp(String.raw`^(.+?)\s*\((${SIZE_SRC})\)\s*$`);

// Page-footer fallback patterns, used ONLY when scanRecordInfo could not
// recover the record's own footer strings (a degraded/truncated extraction).
// On a healthy record the stripper matches the EXACT footer lines built from
// the scanned metadata instead — a prefix pattern would otherwise silently
// delete a user-authored body line or subject title that merely starts the
// same way (evidence alteration). Lockstep with talking_parents.py.
const FOOTER_FALLBACK_RES = [
  /^Complete Message Record - .+$/,
  /^All times presented in .+$/,
  /^Record Generated By TalkingParents\b.*$/,
  /^Unique Authentication Code:.*$/,
];

// Record-level metadata, scanned from the raw (pre-strip) lines.
const PARTIES_RE = /^Complete Message Record - (.+?) and (.+)$/;
const TZ_LABEL_RE = /^All times presented in (.+?)\s*$/;
const GENERATED_RE = new RegExp(
  `^Record Generated By TalkingParents on (${DATE_SRC}) at (${TIME_SRC})$`,
);
const AUTH_CODE_RE = /^Unique Authentication Code: ([A-Z0-9][A-Z0-9-]+) Page \d+ of \d+$/;
const REQUESTED_BY_RE = /^Record Requested by (.+?)\s*$/;

export class MalformedTalkingParentsError extends Error {
  constructor(msg: string) {
    super(msg);
    this.name = "MalformedTalkingParentsError";
  }
}

// --- Canonical row + metadata shapes (6-column, subject-bearing) ---

export interface CanonicalRow {
  timestamp: string;
  sender: string;
  recipient: string;
  subject: string;
  body: string;
  attachments: string;
}

export interface AttachmentItem {
  filename: string;
  size: string;
}

export interface Receipt {
  kind: "viewed" | "downloaded";
  name: string;
  file?: string;
  timestamp: string; // UTC ISO
}

export interface SourceMetadata {
  source: string;
  message_number: number;
  subject: string;
  sender_role: "party" | "other";
  attachments_structured: AttachmentItem[];
  receipts: Receipt[];
}

export interface RecordInfo {
  party_a: string;
  party_b: string;
  timezone_label: string;
  generated_raw: string;
  authentication_code: string;
  requested_by: string;
}

export interface IngestStats {
  party_a: string;
  party_b: string;
  out_of_set_senders: string[];
  message_count: number;
  // TalkingParents has no moderation/rejection concept (the record certifies
  // completeness) — the shared stats contract carries accepted/rejected.
  accepted_count: number;
  rejected_count: number;
  non_printables_stripped: number;
  date_range: { start: string | null; end: string | null };
  timezone: string;
  // True when the record's zone label was unmapped and no override was given.
  timezone_assumed: boolean;
  subject_count: number;
  viewed_receipt_count: number;
  download_receipt_count: number;
  page_count?: number;
}

export interface CaseMeta {
  id: string;
  privacy_class: string;
  prompt_vars: {
    party_a: string;
    party_b: string;
    format: string;
    time_period: string;
    context: string;
  };
  known_patterns: string[];
  notes: string;
  source_timezone?: string;
  predicted_timezone?: string;
  user_timezone?: string;
  timezone_assumed?: boolean;
}

export interface IngestResult {
  rows: CanonicalRow[];
  source_metas: SourceMetadata[];
  stats: IngestStats;
  csv: string;
  case_meta: CaseMeta;
  record_info: RecordInfo;
  warnings: string[];
}

interface NaiveDt {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

export interface ParsedMessage {
  sender: string;
  dt: NaiveDt;
  subject: string;
  body: string;
  attachments: AttachmentItem[];
  receipts: { kind: "viewed" | "downloaded"; name: string; file?: string; dt: NaiveDt }[];
}

// --- Stage 1-2: record info + footer strip ---

export function scanRecordInfo(pages: string[]): RecordInfo {
  const info: RecordInfo = {
    party_a: "",
    party_b: "",
    timezone_label: "",
    generated_raw: "",
    authentication_code: "",
    requested_by: "",
  };
  for (const text of pages) {
    for (const line of text.split("\n")) {
      if (!info.party_a) {
        const m = line.match(PARTIES_RE);
        if (m) {
          info.party_a = m[1].trim();
          info.party_b = m[2].trim();
        }
      }
      if (!info.timezone_label) {
        const m = line.match(TZ_LABEL_RE);
        if (m) info.timezone_label = m[1].trim();
      }
      if (!info.generated_raw) {
        const m = line.match(GENERATED_RE);
        if (m) info.generated_raw = `${m[1]} at ${m[2]}`;
      }
      if (!info.authentication_code) {
        const m = line.match(AUTH_CODE_RE);
        if (m) info.authentication_code = m[1];
      }
      if (!info.requested_by) {
        const m = line.match(REQUESTED_BY_RE);
        if (m) info.requested_by = m[1].trim();
      }
    }
  }
  return info;
}

// Build a line predicate for the running footer. Prefers EXACT footer
// strings reconstructed from the scanned record metadata (identical on every
// page), so user-authored prose that merely starts like a footer line ("All
// times presented in the parenting plan…") is never deleted. Each of the
// four footer line types independently falls back to its loose prefix
// pattern only when the scan failed to recover that field. Lockstep with
// talking_parents.py _footer_matcher.
function footerMatcher(record: RecordInfo): (line: string) => boolean {
  const exact = new Set<string>();
  const fallbacks: RegExp[] = [];

  if (record.party_a && record.party_b) {
    exact.add(`Complete Message Record - ${record.party_a} and ${record.party_b}`);
  } else {
    fallbacks.push(FOOTER_FALLBACK_RES[0]);
  }
  if (record.timezone_label) {
    exact.add(`All times presented in ${record.timezone_label}`);
  } else {
    fallbacks.push(FOOTER_FALLBACK_RES[1]);
  }
  if (record.generated_raw) {
    exact.add(`Record Generated By TalkingParents on ${record.generated_raw}`);
  } else {
    fallbacks.push(FOOTER_FALLBACK_RES[2]);
  }
  let authRe: RegExp | null = null;
  if (record.authentication_code) {
    const esc = record.authentication_code.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    authRe = new RegExp(
      `^Unique Authentication Code: ${esc} Page \\d+ of \\d+\\s*$`,
    );
  } else {
    fallbacks.push(FOOTER_FALLBACK_RES[3]);
  }

  return (line: string): boolean => {
    if (exact.has(line)) return true;
    if (authRe !== null && authRe.test(line)) return true;
    return fallbacks.some((rx) => rx.test(line));
  };
}

export function stripFooters(
  pages: string[],
  record: RecordInfo,
): { lines: string[]; stripped: number } {
  const isFooter = footerMatcher(record);
  const lines: string[] = [];
  let stripped = 0;
  for (const text of pages) {
    for (const line of text.split("\n")) {
      if (isFooter(line)) {
        stripped += 1;
        continue;
      }
      lines.push(line);
    }
  }
  return { lines, stripped };
}

// --- Stage 3: timezone resolution ---

/**
 * Resolve the IANA zone for the record's wall-clock times.
 *
 * Order: the record's own declared label wins (Windows-ID map, then the
 * label as raw IANA) — a TalkingParents record certifies its display zone,
 * so a caller-supplied zone must NEVER silently override it. Only when the
 * label is unmapped does `fallbackTimezone` apply (a user's pick — not an
 * assumption); with no fallback either, DEFAULT_ASSUMED_TIMEZONE is used
 * with assumed=true.
 *
 * DELIBERATE divergence from the Python twin: there `input_timezone` is a
 * hard override, because the CLI flag is an expert's explicit act. The
 * browser option is plumbing shared across adapters (see PdfIngestOptions),
 * where fallback semantics are the safe contract.
 */
export function resolveTimezone(
  timezoneLabel: string,
  fallbackTimezone?: string,
): { tz: string; assumed: boolean } {
  const label = (timezoneLabel ?? "").trim();
  const mapped = TZ_LABEL_TO_IANA[label.toLowerCase()];
  if (mapped) return { tz: mapped, assumed: false };
  if (label && IANAZone.isValidZone(label)) return { tz: label, assumed: false };
  if (fallbackTimezone) {
    if (!IANAZone.isValidZone(fallbackTimezone)) {
      throw new MalformedTalkingParentsError(
        `Unknown timezone: ${fallbackTimezone}`,
      );
    }
    return { tz: fallbackTimezone, assumed: false };
  }
  return { tz: DEFAULT_ASSUMED_TIMEZONE, assumed: true };
}

/**
 * Parse "M/D/YYYY" + "H:MM:SS AM/PM" to naive datetime components, or null.
 * The range checks are the header validator: a header-shaped line with an
 * impossible date (2/30) or hour (0/13 on a 12-hour clock) is a
 * body-embedded look-alike, kept as body — never split, never a crash.
 * Lockstep with talking_parents.py _parse_mdy_hms.
 */
export function parseMdyHms(dateS: string, timeS: string): NaiveDt | null {
  const dm = dateS.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  const tm = timeS.match(/^(\d{1,2}):(\d{2}):(\d{2}) ([AP]M)$/);
  if (!dm || !tm) return null;
  const month = parseInt(dm[1], 10);
  const day = parseInt(dm[2], 10);
  const year = parseInt(dm[3], 10);
  const hour12 = parseInt(tm[1], 10);
  const minute = parseInt(tm[2], 10);
  const second = parseInt(tm[3], 10);
  if (hour12 < 1 || hour12 > 12) return null;
  const hour = (hour12 % 12) + (tm[4] === "PM" ? 12 : 0);
  // luxon marks an out-of-range calendar date/time invalid (never rolls
  // over), matching Python's strptime ValueError → None.
  const dt = DateTime.utc(year, month, day, hour, minute, second);
  if (!dt.isValid) return null;
  return { year, month, day, hour, minute, second };
}

/**
 * Localize a naive wall-clock datetime to `tzName` (DST-aware) and render UTC
 * ISO-8601 with a 'Z' suffix. During the autumn fall-back the wall clock is
 * ambiguous; the record prints no offset, so the instant is genuinely
 * unknowable. luxon resolves the overlap to the earlier (pre-transition)
 * offset — matching the Python adapter's explicit fold=0.
 */
export function toUtcIso(dt: NaiveDt, tzName: string): string {
  const local = DateTime.fromObject(
    {
      year: dt.year,
      month: dt.month,
      day: dt.day,
      hour: dt.hour,
      minute: dt.minute,
      second: dt.second,
    },
    { zone: tzName },
  );
  if (!local.isValid) {
    throw new MalformedTalkingParentsError(
      `bad datetime in ${tzName}: ${local.invalidExplanation ?? local.invalidReason}`,
    );
  }
  return local.toUTC().toFormat("yyyy-LL-dd'T'HH:mm:ss'Z'");
}

// --- Stage 4: parse messages ---

export interface ParseCounts {
  viewed_receipts: number;
  download_receipts: number;
  subjects: number;
  orphan_receipts: number;
}

/**
 * Walk footer-stripped lines and build messages.
 *
 * Wrap tolerance (2026-07-11 fidelity review): PDF hard-wraps break
 * structural lines at a space — a download receipt can split before its
 * "AM/PM", a long attachment entry before its size. Every structural pattern
 * is tried on the line itself, then wrap-joined with the next one or two
 * lines (a join stops before any line that is already structure on its own).
 * Multi-file attachment lists are consumed as continuation items.
 *
 * Subject titles are recognized retroactively at the `Created by:` line: the
 * title is the run of non-structural lines since the last structural line —
 * but joined across its wrap ONLY when that run is provably pure title (the
 * boundary above is a receipt or a Created line, after which no body can
 * resume). After a message header or attachment the run may be an open body
 * followed by the title, so it falls back to the single preceding line.
 * Title lines already accumulated into the open message's body are popped
 * back out. Returns keptAsBody: header-shaped lines whose date failed
 * validation, preserved as body text. Lockstep with talking_parents.py
 * _parse_messages.
 */
export function parseMessages(lines: string[]): {
  messages: ParsedMessage[];
  keptAsBody: string[];
  counts: ParseCounts;
} {
  const messages: ParsedMessage[] = [];
  const keptAsBody: string[] = [];
  const counts: ParseCounts = {
    viewed_receipts: 0,
    download_receipts: 0,
    subjects: 0,
    orphan_receipts: 0,
  };
  let currentSubject = "";
  let cur: {
    sender: string;
    dt: NaiveDt;
    subject: string;
    body: string[];
    attachments: AttachmentItem[];
    receipts: ParsedMessage["receipts"];
  } | null = null;

  const flush = (): void => {
    if (cur !== null) {
      messages.push({
        sender: cur.sender,
        dt: cur.dt,
        subject: cur.subject,
        body: cur.body.join("\n").trim(),
        attachments: cur.attachments,
        receipts: cur.receipts,
      });
      cur = null;
    }
  };

  type Kind = "said" | "created" | "viewed" | "downloaded" | "attach";
  const classify = (
    text: string,
  ): { kind: Kind; m: RegExpMatchArray } | null => {
    const said = text.match(SAID_RE);
    if (said !== null) return { kind: "said", m: said };
    const created = text.match(CREATED_RE);
    if (created !== null) return { kind: "created", m: created };
    const viewed = text.match(VIEWED_RE);
    if (viewed !== null) return { kind: "viewed", m: viewed };
    const downloaded = text.match(DOWNLOADED_RE);
    if (downloaded !== null) return { kind: "downloaded", m: downloaded };
    const attach = text.match(ATTACH_RE);
    if (attach !== null) return { kind: "attach", m: attach };
    return null;
  };
  const dtOf = (kind: Kind, m: RegExpMatchArray): NaiveDt | null =>
    kind === "downloaded"
      ? parseMdyHms(m[3], m[4])
      : parseMdyHms(m[2], m[3]);
  const structuralAlone = (text: string): boolean => {
    const c = classify(text);
    if (c === null) return false;
    return c.kind === "attach" || dtOf(c.kind, c.m) !== null;
  };

  // Index + kind of the last line consumed as structure. The title walk at
  // each Created line keys on both: after a receipt or a Created line the
  // message box has ended, so the following non-structural run is pure
  // title; after a header/attachment the run may still be body.
  let lastStructural = -1;
  let lastStructuralKind: Kind | null = null;
  let attachRun = false; // directly inside a multi-line Attached File(s) list
  const n = lines.length;
  let i = 0;
  while (i < n) {
    const line = lines[i];

    // --- Attachment continuation run (one file per line, possibly with a
    // long name wrapping onto a second line). Ends at the first line that
    // isn't a continuation item.
    if (attachRun && cur !== null) {
      let contSpan = 0;
      let cm = line.match(ATT_CONT_RE);
      if (cm !== null) {
        contSpan = 1;
      } else if (i + 1 < n) {
        cm = (line + " " + lines[i + 1]).match(ATT_CONT_RE);
        if (cm !== null) contSpan = 2;
      }
      if (contSpan > 0 && cm !== null) {
        cur.attachments.push({ filename: cm[1].trim(), size: cm[2] });
        lastStructural = i + contSpan - 1;
        lastStructuralKind = "attach";
        i += contSpan;
        continue;
      }
      attachRun = false;
    }

    // --- Structural match: the line itself first; if nothing matches, retry
    // wrap-joined with the next 1–2 lines (hard wraps break at a space).
    // Joined candidates must be date-valid to count, and a join stops before
    // any line that already IS structure on its own (else a title + the
    // Created line + a header would join into one phantom header).
    let kind: Kind | null = null;
    let m: RegExpMatchArray | null = null;
    let span = 1;
    const single = classify(line);
    if (single !== null) {
      kind = single.kind;
      m = single.m;
    } else {
      for (let extra = 1; extra <= 2; extra++) {
        if (i + extra >= n) break;
        if (structuralAlone(lines[i + extra])) break;
        if (extra === 2) {
          // If the window MINUS its first line is itself a valid wrap-join,
          // the wrap starts at the NEXT line and this line is prose (a
          // one-word signature "M" before a wrapped receipt would otherwise
          // be eaten into the receipt's name — observed on the real record).
          const dropFirst = classify(lines[i + 1] + " " + lines[i + 2]);
          if (
            dropFirst !== null &&
            (dropFirst.kind === "attach" ||
              dtOf(dropFirst.kind, dropFirst.m) !== null)
          ) {
            break;
          }
        }
        const joined = classify(lines.slice(i, i + extra + 1).join(" "));
        if (joined === null) continue;
        if (joined.kind !== "attach" && dtOf(joined.kind, joined.m) === null) {
          continue;
        }
        kind = joined.kind;
        m = joined.m;
        span = 1 + extra;
        break;
      }
    }

    if (kind === "said" && m !== null) {
      const dt = parseMdyHms(m[2], m[3]);
      if (dt === null) {
        // Header-shaped but date-invalid (span 1 only; joins are
        // pre-validated): a quoted look-alike. Keep it verbatim as body —
        // never split a phantom message, never crash.
        keptAsBody.push(line);
        if (cur !== null) cur.body.push(line);
        i += 1;
        continue;
      }
      flush();
      cur = {
        sender: m[1].trim(),
        dt,
        subject: currentSubject,
        body: [],
        attachments: [],
        receipts: [],
      };
      attachRun = false;
      lastStructural = i + span - 1;
      lastStructuralKind = "said";
      i += span;
      continue;
    }

    if (kind === "created" && m !== null && parseMdyHms(m[2], m[3]) !== null) {
      // Title = the non-structural run since the last structural line,
      // wrap-joined (1–3 lines) — but ONLY when that run is provably pure
      // title (bounded by a receipt or Created line). Otherwise fall back
      // to the single preceding line: possible wrapped-title truncation,
      // never body theft.
      const block = lines.slice(lastStructural + 1, i);
      let titleLines: string[];
      if (
        block.length >= 1 &&
        block.length <= 3 &&
        (lastStructuralKind === "viewed" ||
          lastStructuralKind === "downloaded" ||
          lastStructuralKind === "created")
      ) {
        titleLines = block;
      } else {
        titleLines = i > 0 ? [lines[i - 1]] : [];
      }
      if (cur !== null) {
        // Title lines were appended to the open body as they streamed past —
        // pop them back out (only lines that are in fact the trailing lines).
        for (let t = titleLines.length - 1; t >= 0; t--) {
          if (
            cur.body.length > 0 &&
            cur.body[cur.body.length - 1] === titleLines[t]
          ) {
            cur.body.pop();
          }
        }
        flush();
      }
      currentSubject = titleLines
        .map((tl) => tl.trim())
        .join(" ")
        .trim();
      counts.subjects += 1;
      attachRun = false;
      lastStructural = i + span - 1;
      lastStructuralKind = "created";
      i += span;
      continue;
    }

    if ((kind === "viewed" || kind === "downloaded") && m !== null) {
      const dt = dtOf(kind, m);
      if (dt !== null) {
        if (kind === "viewed") {
          counts.viewed_receipts += 1;
          if (cur !== null) {
            cur.receipts.push({ kind: "viewed", name: m[1].trim(), dt });
          } else {
            counts.orphan_receipts += 1;
          }
        } else {
          counts.download_receipts += 1;
          if (cur !== null) {
            cur.receipts.push({
              kind: "downloaded",
              name: m[1].trim(),
              file: m[2],
              dt,
            });
          } else {
            counts.orphan_receipts += 1;
          }
        }
        lastStructural = i + span - 1;
        lastStructuralKind = kind;
        i += span;
        continue;
      }
    }

    if (kind === "attach" && m !== null && cur !== null) {
      // matchAll clones the regex per spec (lastIndex never advances on the
      // module-level original), so no defensive copy — same as ofw.ts. No
      // comma-stripping: the item regex consumes separators itself, so
      // trailing commas belong to the filename (lockstep with the Python
      // twin).
      for (const item of m[1].matchAll(ATT_ITEM_RE)) {
        cur.attachments.push({
          filename: item[1].trim(),
          size: item[2],
        });
      }
      attachRun = true;
      lastStructural = i + span - 1;
      lastStructuralKind = "attach";
      i += span;
      continue;
    }

    if (cur !== null) cur.body.push(line);
    // Lines before the first message (cover prose, Case Information, the
    // Messages-section intro, subject titles) fall through untracked.
    i += 1;
  }
  flush();
  return { messages, keptAsBody, counts };
}

// --- Stage 5: canonical rows + metadata ---

/**
 * Return [party_a, party_b, out_of_set]. The running footer names both
 * parties authoritatively — use it, so a record where one parent never wrote
 * still gets both parties. Fall back to sender frequency only if the footer
 * parse came up empty. Lockstep with talking_parents.py.
 */
export function determineDeclaredParties(
  record: RecordInfo,
  messages: ParsedMessage[],
): [string, string, string[]] {
  let partyA = record.party_a ? sanitizeActor(record.party_a)[0] : "";
  let partyB = record.party_b ? sanitizeActor(record.party_b)[0] : "";
  if (!(partyA && partyB)) {
    // Rank SANITIZED sender names — rows compare their sanitized sender
    // against the declared set, so ranking raw names would make a control-
    // char-bearing party unmatchable (every row demoted to role "other").
    const freq = new Map<string, number>();
    const firstSeen = new Map<string, number>();
    messages.forEach((m, idx) => {
      const name = sanitizeActor(m.sender)[0];
      freq.set(name, (freq.get(name) ?? 0) + 1);
      if (!firstSeen.has(name)) firstSeen.set(name, idx);
    });
    const ordered = [...freq.entries()].sort((a, b) => {
      if (b[1] !== a[1]) return b[1] - a[1];
      return (firstSeen.get(a[0]) ?? 0) - (firstSeen.get(b[0]) ?? 0);
    });
    partyA = partyA || (ordered[0]?.[0] ?? "");
    partyB = partyB || (ordered[1]?.[0] ?? "");
  }
  const declared = new Set([partyA, partyB]);
  const senders = new Set(messages.map((m) => sanitizeActor(m.sender)[0]));
  const outOfSet = [...senders].filter((n) => !declared.has(n)).sort();
  return [partyA, partyB, outOfSet];
}

function attachmentsSummary(items: AttachmentItem[]): string {
  if (items.length === 0) return "";
  const names = items.map((i) => i.filename).join(", ");
  return `${items.length} attachment${items.length !== 1 ? "s" : ""}: ${names}`;
}

function buildRowsAndMetadata(
  messages: ParsedMessage[],
  record: RecordInfo,
  sourceTz: string,
  timezoneAssumed: boolean,
  parseCounts: ParseCounts,
): { rows: CanonicalRow[]; metas: SourceMetadata[]; stats: IngestStats } {
  const [partyA, partyB, outOfSet] = determineDeclaredParties(record, messages);
  const declared = new Set([partyA, partyB]);
  let nonPrintables = 0;

  type Provisional = { ts: string; row: CanonicalRow; meta: SourceMetadata };
  const provisional: Provisional[] = [];
  for (const m of messages) {
    const ts = toUtcIso(m.dt, sourceTz);
    const [body, nBody] = sanitizeBody(m.body);
    const [sender, nSender] = sanitizeActor(m.sender);
    const [subject, nSubj] = sanitizeBody(m.subject);
    nonPrintables += nBody + nSender + nSubj;

    let senderRole: "party" | "other";
    let recipient: string;
    if (declared.has(sender)) {
      senderRole = "party";
      recipient = sender === partyA ? partyB : partyA;
    } else {
      senderRole = "other";
      recipient = "";
    }

    const receipts: Receipt[] = m.receipts.map((r) => ({
      kind: r.kind,
      name: sanitizeActor(r.name)[0],
      ...(r.file !== undefined ? { file: r.file } : {}),
      timestamp: toUtcIso(r.dt, sourceTz),
    }));

    provisional.push({
      ts,
      row: {
        timestamp: ts,
        sender,
        recipient,
        subject,
        body,
        attachments: attachmentsSummary(m.attachments),
      },
      meta: {
        source: ADAPTER_NAME,
        message_number: 0,
        subject,
        sender_role: senderRole,
        attachments_structured: m.attachments,
        receipts,
      },
    });
  }

  // The record orders subjects by most-recent activity; the canonical corpus
  // is chronological. Stable sort preserves within-subject order on ties.
  provisional.sort((a, b) => (a.ts < b.ts ? -1 : a.ts > b.ts ? 1 : 0));
  const rows = provisional.map((p) => p.row);
  const metas = provisional.map((p, i) => ({ ...p.meta, message_number: i + 1 }));

  const dateRange = {
    start: rows.length > 0 ? rows[0].timestamp : null,
    end: rows.length > 0 ? rows[rows.length - 1].timestamp : null,
  };

  const stats: IngestStats = {
    party_a: partyA,
    party_b: partyB,
    out_of_set_senders: outOfSet,
    message_count: rows.length,
    accepted_count: rows.length,
    rejected_count: 0,
    non_printables_stripped: nonPrintables,
    date_range: dateRange,
    timezone: sourceTz,
    timezone_assumed: timezoneAssumed,
    subject_count: parseCounts.subjects,
    viewed_receipt_count: parseCounts.viewed_receipts,
    download_receipt_count: parseCounts.download_receipts,
  };
  return { rows, metas, stats };
}

// --- CSV serialization (6-column canonical, RFC 4180 quoting) ---

const CANONICAL_HEADERS = [
  "timestamp",
  "sender",
  "recipient",
  "subject",
  "body",
  "attachments",
] as const;

function csvEscape(cell: string): string {
  if (/[",\n\r]/.test(cell)) {
    return `"${cell.replace(/"/g, '""')}"`;
  }
  return cell;
}

function rowsToCsv(rows: CanonicalRow[]): string {
  const header = CANONICAL_HEADERS.join(",") + "\n";
  if (rows.length === 0) return header;
  const body = rows
    .map((r) => CANONICAL_HEADERS.map((h) => csvEscape(r[h])).join(","))
    .join("\n");
  return header + body + "\n";
}

// --- Case meta builder ---

const TODO_CONTEXT_SENTINEL =
  "TODO: describe the context (e.g. 'their 9-year-old son Murphy')";
const TODO_NOTES_SENTINEL = "TODO: optional freeform notes about this corpus";

function formatTimePeriod(startIso: string, endIso: string): string {
  if (!startIso || !endIso) return "";
  const a = DateTime.fromISO(startIso, { zone: "utc" }).toFormat("LLL yyyy");
  const b = DateTime.fromISO(endIso, { zone: "utc" }).toFormat("LLL yyyy");
  return `${a} – ${b}`;
}

function buildCaseMeta(corpusId: string, stats: IngestStats): CaseMeta {
  const meta: CaseMeta = {
    id: corpusId,
    privacy_class: "private",
    prompt_vars: {
      party_a: stats.party_a,
      party_b: stats.party_b,
      format:
        "TalkingParents Complete Message Record PDF export, canonicalized to CSV (schema v1)",
      time_period: formatTimePeriod(
        stats.date_range.start ?? "",
        stats.date_range.end ?? "",
      ),
      context: TODO_CONTEXT_SENTINEL,
    },
    known_patterns: [],
    notes: TODO_NOTES_SENTINEL,
  };
  if (stats.timezone) {
    meta.source_timezone = stats.timezone;
    meta.predicted_timezone = stats.timezone;
    meta.user_timezone = stats.timezone;
  }
  if (stats.timezone_assumed) meta.timezone_assumed = true;
  return meta;
}

// --- Public entry points ---

export interface IngestOptions {
  /** Slug for the case (sets meta.id). Required by the cloud-server. */
  corpusId: string;
  /** Optional progress callback (per-page during pdf extraction). */
  onProgress?: (p: PdfExtractProgress) => void;
  /**
   * IANA zone override for the record's wall-clock timestamps. Normally
   * unnecessary — the record declares its own display zone; use this when
   * the label is unmapped or the record was generated in the wrong zone.
   */
  defaultTimezone?: string;
}

/**
 * Pure-text parsing path: takes already-extracted page texts and returns the
 * canonical IngestResult. Exposed so unit tests can exercise the parser
 * without running pdf.js in Node.
 *
 * Throws MalformedTalkingParentsError if no valid TalkingParents message
 * headers are found or the supplied timezone override is unknown.
 */
export function ingestTalkingParentsFromPages(
  pagesRaw: string[],
  opts: { corpusId: string; defaultTimezone?: string },
): IngestResult {
  if (pagesRaw.length === 0) {
    throw new MalformedTalkingParentsError("PDF contained no extractable text");
  }

  const record = scanRecordInfo(pagesRaw);
  const { tz, assumed } = resolveTimezone(record.timezone_label, opts.defaultTimezone);
  const { lines } = stripFooters(pagesRaw, record);

  const { messages, keptAsBody, counts } = parseMessages(lines);
  if (messages.length === 0) {
    throw new MalformedTalkingParentsError(
      "No TalkingParents messages detected — likely wrong PDF format.",
    );
  }

  const { rows, metas, stats: baseStats } = buildRowsAndMetadata(
    messages,
    record,
    tz,
    assumed,
    counts,
  );
  const stats: IngestStats = { ...baseStats, page_count: pagesRaw.length };
  const csv = rowsToCsv(rows);
  const case_meta = buildCaseMeta(opts.corpusId, stats);

  const warnings: string[] = [];
  if (assumed) {
    warnings.push(
      `Couldn't map the record's time zone label (${record.timezone_label || "missing"}) — assumed ${tz}; verify/correct`,
    );
  }
  if (keptAsBody.length > 0) {
    warnings.push(
      `${keptAsBody.length} header-shaped line(s) kept as body (look-alikes)`,
    );
  }

  return {
    rows,
    source_metas: metas,
    stats,
    csv,
    case_meta,
    record_info: record,
    warnings,
  };
}

/**
 * Ingest a TalkingParents Complete Message Record PDF into the canonical CSV
 * shape. Throws MalformedTalkingParentsError if the PDF doesn't contain any
 * valid TalkingParents message headers (likely wrong format).
 */
export async function ingestTalkingParentsPdf(
  pdfData: ArrayBuffer | Uint8Array,
  opts: IngestOptions,
): Promise<IngestResult> {
  const pagesRaw = await extractPdfTextEol(pdfData, opts.onProgress);
  return ingestTalkingParentsFromPages(pagesRaw, {
    corpusId: opts.corpusId,
    defaultTimezone: opts.defaultTimezone,
  });
}
